import { inject } from "@angular/core";
import { tapResponse } from "@ngrx/operators";
import { patchState, signalStore, withMethods, withState } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import {
  setPropLoaded,
  withReqState,
  setPropError, setPropLoading,
  setPropInit
} from "@application/data-access";
import { EMPTY, Observable, from, pipe, switchMap, tap } from "rxjs";
import { DxOdataStoreService } from "@shared/components/dx-data-grid/data-access";
import { APP_CONFIG } from "@application/config";
import { HttpErrorResponse } from "@angular/common/http";
import DataSource from "devextreme/data/data_source";
import { IGood, IInventoryBalance as IStockAdjustment, IManualAdjust, IPurchase, ISale, ISaleCondition, ISupplier, IInventoryBalance, IStockOnDate, TSaleSettings } from './entities/goods.interface';
import { GoodsService } from './infra/goods.service';
import { AlertStore } from "@alert/data-access";
import { ErrorStore } from "@application/errors/data-access";
import { PersonService } from "@domains/person/register/data";
import { PermissionNumber, SelfUserStore } from "@domains/person/user/self/data";

export interface IGoodsStore {
    good: IGood;
    showGoodModal: boolean;
    showSupplierModal: boolean;
    showConditionModal: boolean;
    showManualAdjustmentModal: boolean;
    goods: DataSource;
    purchase: IPurchase;
    sale: ISale;
    sales: DataSource;
    conditions: ISaleCondition[];
    sellers: DataSource;
    suppliers: DataSource;
    supplier: ISupplier,
    saleSettings: TSaleSettings;
    saleConditions: DataSource;
    saleCondition: ISaleCondition,
    manualAdjustments: DataSource;
    manualAdjustment: IManualAdjust;
    stockAdjustment: IStockAdjustment[];
    allGoods: IGood[];
  }

export const goodsInitialState: IGoodsStore = {
    good: null,
    showGoodModal: false,
    showSupplierModal: false,
    showConditionModal: false,
    showManualAdjustmentModal: false,
    goods: null,
    purchase: null,
    sale: null,
    sales: null,
    conditions: null,
    sellers: null,
    suppliers: null,
    supplier: null,
    saleSettings: null,
    saleConditions: null,
    saleCondition: null,
    manualAdjustments: null,
    manualAdjustment: null,
    stockAdjustment: null,
    allGoods: [],
};

const TEMP_AUTH_PERMISSION_GUARD_PATH = 'TEMPORARY_AUTH_MODAL.PERMISSION_GUARD';

export const GoodsStore = signalStore(
    { providedIn: 'root' },
    withState<IGoodsStore>(goodsInitialState),
    withMethods((
        store,
        goodsService = inject(GoodsService),
        odataService = inject(DxOdataStoreService),
        personService = inject(PersonService),
        alertStore = inject(AlertStore),
        errorStore = inject(ErrorStore),
        dxOdataService = inject(DxOdataStoreService),
        selfUserStore = inject(SelfUserStore),
        apiUrl = inject(APP_CONFIG).apiUrl
        ) => {

        const ensurePermission = async (
            permission: PermissionNumber,
            translationKey: string,
        ): Promise<boolean> => {
            const hasPermission = await selfUserStore.ensurePermissions([permission], { interactive: false });

            if (hasPermission) {
                return true;
            }

            return selfUserStore.ensurePermissions([permission], {
                title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${translationKey}.TITLE`,
                message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${translationKey}.MESSAGE`,
            });
        };

        const guardPermission = <T>(
            permission: PermissionNumber,
            translationKey: string,
            onDenied: () => void,
            work: () => Observable<T>,
        ): Observable<T> =>
            from(ensurePermission(permission, translationKey)).pipe(
                switchMap((hasPermission) => {
                    if (!hasPermission) {
                        onDenied?.();
                        return EMPTY as Observable<T>;
                    }

                    return work();
                })
            );

        const hasSaleDiscount = (sale: Partial<ISale> | null | undefined): boolean =>
            !!sale?.Items?.some((item) =>
                (item?.AmountDiscount ?? 0) > 0 || (item?.DiscountPercentage ?? 0) > 0
            );

        return ({
            getGoodsDataSource(): DataSource {
                return dxOdataService.fromOdata({
                  url: apiUrl.odata_goods_service,
                  metadata: 'Goods',
                  key: 'GoodId',
                  permissionGuard: {
                    permissions: [PermissionNumber.ProdutoObterTodos],
                    translationKey: 'PRODUCT_LIST',
                  },
                });
            },

            getAllSuppliers(): DataSource  {
                return dxOdataService.fromOdata({
                    url: apiUrl.odata_goods_service,
                    metadata: 'Suppliers',
                    key: 'PersonId',
                    permissionGuard: {
                        permissions: [PermissionNumber.FornecedorObterTodos],
                        translationKey: 'SUPPLIER_LIST',
                    },
                });
            },

            getAllConditions():  DataSource {
                return odataService.fromOdata({
                    url: apiUrl.odata_goods_service,
                    metadata: 'SaleConditions',
                    key: 'SaleConditionId',
                    permissionGuard: {
                        permissions: [PermissionNumber.CondicaoVendaProdutoObterTodos],
                        translationKey: 'SALE_CONDITION_LIST',
                    },
                })
            },

            getAllManualAdjustments(): DataSource  {
                return odataService.fromOdata({
                    url: apiUrl.odata_goods_service,
                    metadata: 'StocksAdjustments',
                    key: 'StockAdjustmentId',
                    params: {
                        '$orderby': 'AdjustmentOn desc',
                    },
                    permissionGuard: {
                        permissions: [PermissionNumber.AjusteEstoqueProdutoObterTodos],
                        translationKey: 'PRODUCT_STOCK_ADJUSTMENT_LIST',
                    },
                })
            },

            getInventoryBalance(): DataSource {
                return dxOdataService.fromOdata({
                  url: apiUrl.odata_goods_service,
                  metadata: 'Goods',
                  key: 'GoodId',
                  params: {
                    $select: 'GoodId,Description,QuantityStock',
                  },
                  permissionGuard: {
                    permissions: [PermissionNumber.ProdutoObterTodos],
                    translationKey: 'PRODUCT_LIST',
                  },
                });
            },

            setPropInitTo: (keys: string[]) => {
                patchState(store, <NonNullable<unknown>>{ ...setPropInit(...keys) });
            },

            clearGoods() {
                patchState(store, goodsInitialState, setPropInit(
                    'getGood', 'createGood', 'updateGood', 'deleteGood',
                    'getPurchase', 'createPurchase', 'updatePurchase', 'deletePurchase',
                    'getSale', 'submitSale', 'deleteSale', 'getSaleConditions', 'getSaleSettings', 'updateSaleSettings',
                    'getSupplier', 'updateSupplier', 'createSupplier',
                    'getSaleCondition', 'createSaleCondition', 'updateSaleCondition', 'deleteSaleCondition',
                    'getManualAdjustment', 'createManualAdjustment', 'updateManualAdjustment', 'deleteManualAdjustment', 'adjustStock',
                    'submitInventoryBalance', 'getAllGoods'
                ))
            },
            
            setPropsBlankState: (props: string[]) => {
                patchState(store, {...setPropInit(...props) })
            },

            clearOpenGood(): void {
                patchState(store, {
                    good: null,
                }, setPropInit(
                    'getGood', 'createGood', 'updateGood', 'deleteGood',
                ))
            },

            clearOpenSupplier(): void {
                patchState(store, {
                    supplier: null,
                }, setPropInit(
                    'getSupplier', 'createSupplier', 'updateSupplier'
                ))
            },

            clearOpenSaleCondition(): void {
                patchState(store, {
                    saleCondition: null,
                }, setPropInit(
                    'getSaleCondition', 'createSaleCondition', 'updateSaleCondition', 'deleteSaleCondition'
                ))
            },

            clearOpenManualAdjustment(): void {
                patchState(store, {
                    manualAdjustment: null,
                }, setPropInit(
                    'getManualAdjustment', 'createManualAdjustment', 'updateManualAdjustment', 'deleteManualAdjustment', 'adjustStock'
                ))
            },

            toggleGoodModal: (showGoodModal = !store.showGoodModal()) => {
                patchState(store, { showGoodModal });
            },

            toggleSupplierModal: (showSupplierModal = !store.showSupplierModal()) => {
                patchState(store, { showSupplierModal });
            },

            toggleConditionModal: (showConditionModal = !store.showConditionModal()) => {
                patchState(store, { showConditionModal });
            },

            toggleManualAdjustmentModal: (showManualAdjustmentModal = !store.showManualAdjustmentModal()) => {
                patchState(store, { showManualAdjustmentModal });
            },

            getGoodById: rxMethod<string>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('getGood') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.ProdutoObterPorId,
                        'PRODUCT_VIEW',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('getGood', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.getGoodById(payload)
                            .pipe(
                                tapResponse({
                                    next: (good) => patchState<NonNullable<unknown>>(store, { good, ...setPropLoaded('getGood') }),
                                    error: (error: HttpErrorResponse) => {
                                        console.error(error);
                                        patchState( store, <NonNullable<unknown>>{ ...setPropError('getGood', error) });
                                        errorStore.setHttpError(error)
                                    }
                                })
                            )
                    )
                )
            )),

            getStockOnDate: rxMethod<{ goodId: string; on: string }>(pipe(
                switchMap((payload) =>
                    goodsService.getStockOnDate(payload.goodId, payload.on)
                        .pipe(
                            tapResponse({
                                next: (stockOnDate) => {
                                    const currentGood = store.good();
                                    if (currentGood && currentGood.GoodId === payload.goodId) {
                                        patchState(store, {
                                            good: { ...currentGood, QuantityStock: stockOnDate.Quantity ?? currentGood.QuantityStock }
                                        });
                                    }
                                },
                                error: (error: HttpErrorResponse) => {
                                    console.error(error);
                                    errorStore.setHttpError(error);
                                }
                            })
                        )
                )
            )),
            createGood: rxMethod<IGood>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('createGood') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.ProdutoAdicionar,
                        'PRODUCT_CREATE',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('createGood', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.createGood(payload)
                            .pipe(
                                tapResponse({
                                    next: (good) => {
                                        patchState<NonNullable<unknown>>(store, { good, ...setPropLoaded('createGood') })
                                    },
                                    error: (error: HttpErrorResponse) => {
                                        patchState( store, <NonNullable<unknown>>{ ...setPropError('createGood', error) });
                                    }
                                })
                            )
                    )
                )
            )),
            updateGood: rxMethod<IGood>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('updateGood') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.ProdutoAtualizar,
                        'PRODUCT_UPDATE',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('updateGood', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.updateGood(payload)
                            .pipe(
                                tapResponse({
                                    next: (good) => {
                                        patchState<NonNullable<unknown>>(store, { good, ...setPropLoaded('updateGood') })
                                    },
                                    error: (error: HttpErrorResponse) => {
                                        patchState( store, <NonNullable<unknown>>{ ...setPropError('updateGood', error) });
                                    }
                                })
                            )
                    )
                )
            )),

            deleteGood: rxMethod<string>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('deleteGood') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.ProdutoRemover,
                        'PRODUCT_DELETE',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('deleteGood', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.removeGood(payload)
                            .pipe(
                                tapResponse({
                                    next: (good) => {
                                        patchState<NonNullable<unknown>>(store, { good, ...setPropLoaded('deleteGood') })
                                    },
                                    error: (error: HttpErrorResponse) => {
                                        patchState( store, <NonNullable<unknown>>{ ...setPropError('deleteGood', error) });
                                    }
                                })
                            )
                    )
                )
            )),

            getAllPurchases(): DataSource {
                return odataService.fromOdata({
                    url: apiUrl.odata_goods_service,
                    metadata: 'Purchases',
                    key: 'PurchaseId',
                    params: {
                        $select: 'PurchaseId,CountGoods,GoodId,GoodDescription,AmountTotal,CountTotal,SupplierId,SupplierName,AddedOn,Document,Picture1StorageId'
                     },
                    permissionGuard: {
                        permissions: [PermissionNumber.CompraObterTodos],
                        translationKey: 'PURCHASE_LIST',
                    }
                 });
            },

            getSaleSettings: rxMethod<string>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('getSaleSettings') })),
                switchMap((payload) => goodsService.getSaleSettings(payload)
                .pipe(
                    tapResponse({
                        next: (saleSettings) => patchState<NonNullable<unknown>>(store, { saleSettings, ...setPropLoaded('getSaleSettings') }),
                        error: (error: HttpErrorResponse) => {
                            console.error(error);
                            patchState( store, <NonNullable<unknown>>{ ...setPropError('getSaleSettings', error) })
                        }
                    })
                ))
            )),

            updateSaleSettings: rxMethod<Partial<TSaleSettings>>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('updateSaleSettings') })),
                switchMap((payload) => goodsService.updateSaleSettings(payload)
                .pipe(
                    tapResponse({
                        next: (saleSettings) => {
                            patchState<NonNullable<unknown>>(store, { saleSettings, ...setPropLoaded('updateSaleSettings') })
                        },
                        error: (error: HttpErrorResponse) => {
                            patchState( store, <NonNullable<unknown>>{ ...setPropError('updateSaleSettings', error) });
                        }
                    })
                ))
            )),

            getPurchaseById: rxMethod<string>(pipe(
                tap(() => patchState(store, { ...setPropLoading('getPurchase') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.CompraObterPorId,
                        'PURCHASE_VIEW',
                        () => patchState(store, { ...setPropError('getPurchase', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.getPurchaseById(payload)
                        .pipe(
                            tapResponse({
                                next: (purchase) => patchState(store, { purchase, ...setPropLoaded('getPurchase') }),
                                error: (error: HttpErrorResponse) => {
                                    patchState( store, { ...setPropError('getPurchase', error) })
                                }
                            })
                        )
                    )
                )
            )),

            createPurchase: rxMethod<Partial<IPurchase>>(pipe(
                tap(() => patchState(store, { ...setPropLoading('createPurchase') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.CompraAdicionar,
                        'PURCHASE_CREATE',
                        () => patchState(store, { ...setPropError('createPurchase', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.createPurchase(payload)
                        .pipe(
                            tapResponse({
                                next: (purchase) => {
                                    patchState(store, { purchase, ...setPropLoaded('createPurchase') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    errorStore.setHttpError(error);
                                    patchState( store, { ...setPropError('createPurchase', error) })
                                }
                            })
                        )
                    )
                )
            )),

            updatePurchase: rxMethod<Partial<IPurchase>>(pipe(
                tap(() => patchState(store, { ...setPropLoading('updatePurchase') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.CompraAtualizar,
                        'PURCHASE_UPDATE',
                        () => patchState(store, { ...setPropError('updatePurchase', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.updatePurchase(payload)
                        .pipe(
                            tapResponse({
                                next: (purchase) => {
                                    patchState(store, { purchase, ...setPropLoaded('updatePurchase') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    errorStore.setHttpError(error);
                                    patchState( store, { ...setPropError('updatePurchase', error) })
                                }
                            })
                        )
                    )
                )
            )),

            deletePurchase: rxMethod<string>(pipe(
                tap(() => patchState(store, { ...setPropLoading('deletePurchase') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.CompraRemover,
                        'PURCHASE_DELETE',
                        () => patchState(store, { ...setPropError('deletePurchase', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.removePurchase(payload)
                        .pipe(
                            tapResponse({
                                next: () => {
                                    patchState(store, { purchase: null, ...setPropLoaded('deletePurchase') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    errorStore.setHttpError(error);
                                    patchState( store, { ...setPropError('deletePurchase', error) })
                                }
                            })
                        )
                    )
                )
            )),

            getSaleConditions: rxMethod<void>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('getSaleConditions') })),
                switchMap(() =>
                    guardPermission(
                        PermissionNumber.CondicaoVendaProdutoObterTodos,
                        'SALE_CONDITION_LIST',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('getSaleConditions', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.getSaleConditions({
                            '$orderby': 'Description',
                            '$filter': 'IsActive eq true',
                        })
                        .pipe(
                            tapResponse({
                                next: ({value}) => patchState<NonNullable<unknown>>(store, { conditions: value }, setPropLoaded('getSaleConditions')),
                                error: (error: HttpErrorResponse) => {
                                    console.error(error);
                                }
                            })
                        )
                    )
                )
            )),

            getSaleById: rxMethod<string>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('getSale') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.VendaObterPorId,
                        'SALE_VIEW',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('getSale', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.getSaleById(payload)
                        .pipe(
                            tapResponse({
                                next: (sale) => patchState<NonNullable<unknown>>(store, { sale, ...setPropLoaded('getSale') }),
                                error: (error: HttpErrorResponse) => {
                                    console.error(error);
                                    patchState( store, <NonNullable<unknown>>{ ...setPropError('getSale', error) });
                                    errorStore.setHttpError(error)
                                }
                            })
                        )
                    )
                )
            )),

            createSale: rxMethod<Partial<ISale>>(pipe(
                tap(() => patchState(store, { ...setPropLoading('submitSale') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.VendaAdicionar,
                        'SALE_CREATE',
                        () => patchState(store, { ...setPropError('submitSale', new Error('ALERT.ERROR_401')) }),
                        () => {
                            const performSale = () => goodsService.createSale(payload)
                                .pipe(
                                    tapResponse({
                                        next: (sale) => {
                                            patchState(store, { sale, ...setPropLoaded('submitSale') })
                                            alertStore.create({ message: 'GOODS.SALES.SALE.CREATED_SUCESSFULLY_ALERT', title: '', type: 'success' });
                                        },
                                        error: (error: HttpErrorResponse) => {
                                            console.error(error);
                                            patchState( store, { ...setPropError('submitSale', error) })
                                            errorStore.setHttpError(error)
                                        }
                                    })
                                );

                            if (!hasSaleDiscount(payload)) {
                                return performSale();
                            }

                            return guardPermission(
                                PermissionNumber.VendaAdicionarDesconto,
                                'SALE_CREATE_WITH_DISCOUNT',
                                () => patchState(store, { ...setPropError('submitSale', new Error('ALERT.ERROR_401')) }),
                                performSale
                            );
                        }
                    )
                )
            )),

            updateSale: rxMethod<Partial<ISale>>(pipe(
                tap(() => patchState(store, { ...setPropLoading('submitSale') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.VendaAtualizar,
                        'SALE_UPDATE',
                        () => patchState(store, { ...setPropError('submitSale', new Error('ALERT.ERROR_401')) }),
                        () => {
                            const performSale = () => goodsService.updateSale(payload)
                                .pipe(
                                    tapResponse({
                                        next: (sale) => {
                                            patchState(store, { sale, ...setPropLoaded('submitSale') })
                                            alertStore.create({ message: 'GOODS.SALES.SALE.UPDATED_SUCCESSFULY_ALERT', title: '', type: 'success' });
                                        },
                                        error: (error: HttpErrorResponse) => {
                                            console.error(error);
                                            patchState( store, { ...setPropError('submitSale', error) });
                                            errorStore.setHttpError(error)
                                        }
                                    })
                                );

                            if (!hasSaleDiscount(payload)) {
                                return performSale();
                            }

                            return guardPermission(
                                PermissionNumber.VendaAdicionarDesconto,
                                'SALE_CREATE_WITH_DISCOUNT',
                                () => patchState(store, { ...setPropError('submitSale', new Error('ALERT.ERROR_401')) }),
                                performSale
                            );
                        }
                    )
                )
            )),

            deleteSale: rxMethod<string>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('deleteSale') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.VendaRemover,
                        'SALE_DELETE',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('deleteSale', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.removeSale(payload)
                        .pipe(
                            tapResponse({
                                next: (sale) => {
                                    patchState<NonNullable<unknown>>(store, { sale, ...setPropLoaded('deleteSale') })
                                    alertStore.create({ message: 'GOODS.SALES.SALE.DELETED_SUCCESSFULLY_ALERT', title: '', type: 'success' });
                                },
                                error: (error: HttpErrorResponse) => {
                                    console.error(error);
                                    patchState( store, <NonNullable<unknown>>{ ...setPropError('deleteSale', error) });
                                    errorStore.setHttpError(error)
                                }
                            })
                        )
                    )
                )
            )),

            getSupplierById: rxMethod<string>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('getSupplier') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.FornecedorObterPorId,
                        'SUPPLIER_VIEW',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('getSupplier', new Error('ALERT.ERROR_401')) }),
                        () => personService.getSupplierById(payload)
                        .pipe(
                            tapResponse({
                                next: (supplier) => patchState<NonNullable<unknown>>(store, { supplier, ...setPropLoaded('getSupplier') }),
                                error: (error: HttpErrorResponse) => {
                                    console.error(error);
                                    patchState( store, <NonNullable<unknown>>{ ...setPropError('getSupplier', error) });
                                    errorStore.setHttpError(error)
                                }
                            })
                        )
                    )
                )
            )),

            createSupplier: rxMethod<ISupplier>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('createSupplier') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.FornecedorAdicionar,
                        'SUPPLIER_CREATE',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('createSupplier', new Error('ALERT.ERROR_401')) }),
                        () => personService.createSupplier(payload)
                        .pipe(
                            tapResponse({
                                next: (supplier) =>{
                                    patchState<NonNullable<unknown>>(store, { supplier, ...setPropLoaded('createSupplier') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState(store, <NonNullable<unknown>>{ ...setPropError('createSupplier', error) });
                                }
                            })
                        )
                    )
                )
            )),

            updateSupplier: rxMethod<ISupplier>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('updateSupplier') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.FornecedorAtualizar,
                        'SUPPLIER_UPDATE',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('updateSupplier', new Error('ALERT.ERROR_401')) }),
                        () => personService.updateSupplier(payload)
                        .pipe(
                            tapResponse({
                                next: (supplier) => {
                                    patchState<NonNullable<unknown>>(store, { supplier, ...setPropLoaded('updateSupplier') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState( store, <NonNullable<unknown>>{ ...setPropError('updateSupplier', error) });
                                }
                            })
                        )
                    )
                )
            )),

            getSaleConditionById: rxMethod<string>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('getSaleCondition') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.CondicaoVendaProdutoObterPorId,
                        'SALE_CONDITION_VIEW',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('getSaleCondition', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.getSaleConditionById(payload)
                        .pipe(
                            tapResponse({
                                next: (saleCondition) => patchState<NonNullable<unknown>>(store, { saleCondition, ...setPropLoaded('getSaleCondition') }),
                                error: (error: HttpErrorResponse) => {
                                    console.error(error);
                                    patchState( store, <NonNullable<unknown>>{ ...setPropError('getSaleCondition', error) });
                                    errorStore.setHttpError(error)
                                }
                            })
                        )
                    )
                )
            )),

            createSaleCondition: rxMethod<ISaleCondition>(pipe(
                tap(() => patchState(store, <Partial<NonNullable<unknown>>>{ ...setPropLoading('createSaleCondition') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.CondicaoVendaProdutoAdicionar,
                        'SALE_CONDITION_CREATE',
                        () => patchState(store, <Partial<NonNullable<unknown>>>{ ...setPropError('createSaleCondition', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.createSaleCondition(payload)
                        .pipe(
                            tapResponse({
                                next: (saleCondition) => {
                                    patchState<NonNullable<unknown>>(store, { saleCondition, ...setPropLoaded('createSaleCondition') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState( store, <NonNullable<unknown>>{ ...setPropError('createSaleCondition', error) });
                                }
                            })
                        )
                    )
                )
            )),

            updateSaleCondition: rxMethod<ISaleCondition>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('updateSaleCondition') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.CondicaoVendaProdutoAtualizar,
                        'SALE_CONDITION_UPDATE',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('updateSaleCondition', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.updateSaleCondition(payload)
                        .pipe(
                            tapResponse({
                                next: (saleCondition) => {
                                    patchState<NonNullable<unknown>>(store, { saleCondition, ...setPropLoaded('updateSaleCondition') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState( store, <NonNullable<unknown>>{ ...setPropError('updateSaleCondition', error) });
                                }
                            })
                        )
                    )
                )
            )),

            deleteSaleCondition: rxMethod<string>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('deleteSaleCondition') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.CondicaoVendaProdutoRemover,
                        'SALE_CONDITION_DELETE',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('deleteSaleCondition', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.removeSaleCondition(payload)
                        .pipe(
                            tapResponse({
                                next: (saleCondition) => {
                                    patchState<NonNullable<unknown>>(store, { saleCondition, ...setPropLoaded('deleteSaleCondition') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState( store, <NonNullable<unknown>>{ ...setPropError('deleteSaleCondition', error) });
                                }
                            })
                        )
                    )
                )
            )),

            getManualAdjustmentById: rxMethod<string>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('getManualAdjustment') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.AjusteEstoqueProdutoObterPorId,
                        'PRODUCT_STOCK_ADJUSTMENT_VIEW',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('getManualAdjustment', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.getManualAdjustmentById(payload)
                        .pipe(
                            tapResponse({
                                next: (manualAdjustment) => patchState<NonNullable<unknown>>(store, { manualAdjustment, ...setPropLoaded('getManualAdjustment') }),
                                error: (error: HttpErrorResponse) => {
                                    console.error(error);
                                    patchState( store, <NonNullable<unknown>>{ ...setPropError('getManualAdjustment', error) });
                                    errorStore.setHttpError(error)
                                }
                            })
                        )
                    )
                )
            )),

            createManualAdjustment: rxMethod<Partial<IManualAdjust>>(pipe(
                tap(() => patchState(store, { ...setPropLoading('createManualAdjustment') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.AjusteEstoqueProdutoAdicionar,
                        'PRODUCT_STOCK_ADJUSTMENT_CREATE',
                        () => patchState(store, { ...setPropError('createManualAdjustment', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.createManualAdjustment(payload)
                        .pipe(
                            tapResponse({
                                next: (manualAdjustment) => {
                                    patchState(store, { manualAdjustment, ...setPropLoaded('createManualAdjustment') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState( store, { ...setPropError('createManualAdjustment', error) });
                                }
                            })
                        )
                    )
                )
            )),

            updateManualAdjustment: rxMethod<Partial<IManualAdjust>>(pipe(
                tap(() => patchState(store, { ...setPropLoading('updateManualAdjustment') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.AjusteEstoqueProdutoAtualizar,
                        'PRODUCT_STOCK_ADJUSTMENT_UPDATE',
                        () => patchState(store, { ...setPropError('updateManualAdjustment', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.updateManualAdjustment(payload)
                        .pipe(
                            tapResponse({
                                next: (manualAdjustment) => {
                                    patchState(store, { manualAdjustment, ...setPropLoaded('updateManualAdjustment') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState( store, { ...setPropError('updateManualAdjustment', error) });
                                }
                            })
                        )
                    )
                )
            )),

            deleteManualAdjustment: rxMethod<string>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('deleteManualAdjustment') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.AjusteEstoqueProdutoRemover,
                        'PRODUCT_STOCK_ADJUSTMENT_DELETE',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('deleteManualAdjustment', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.removeManualAdjustment(payload)
                        .pipe(
                            tapResponse({
                                next: (manualAdjustment) => {
                                    patchState<NonNullable<unknown>>(store, { manualAdjustment, ...setPropLoaded('deleteManualAdjustment') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState( store, <NonNullable<unknown>>{ ...setPropError('deleteManualAdjustment', error) });
                                }
                            })
                        )
                    )
                )
            )),

            adjustStock: rxMethod<Partial<IManualAdjust>>(pipe(
                tap(() => patchState(store, { ...setPropLoading('adjustStock') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.AjusteEstoqueProdutoAdicionar,
                        'PRODUCT_STOCK_ADJUSTMENT_CREATE',
                        () => patchState(store, { ...setPropError('adjustStock', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.adjustStock(payload)
                        .pipe(
                            tapResponse({
                                next: (manualAdjustment) => {
                                    patchState(store, { manualAdjustment, ...setPropLoaded('adjustStock') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState( store, { ...setPropError('adjustStock', error) });
                                }
                            })
                        )
                    )
                )
            )),
            submitInventoryBalance: rxMethod<{ StockAdjustmentViewModel: IInventoryBalance[] }>(pipe(
                tap(() => patchState(store, { ...setPropLoading('submitInventoryBalance') })),
                switchMap((payload) =>
                    guardPermission(
                        PermissionNumber.AjusteEstoqueProdutoAdicionar,
                        'PRODUCT_STOCK_ADJUSTMENT_CREATE',
                        () => patchState(store, { ...setPropError('submitInventoryBalance', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.addRangeStockAdjusment(payload)
                        .pipe(
                            tapResponse({
                                next: ({StockAdjustmentViewModel}) => {
                                    patchState(store, { stockAdjustment: StockAdjustmentViewModel, ...setPropLoaded('submitInventoryBalance') })
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState(store, { ...setPropError('submitInventoryBalance', error) });
                                }
                            })
                        )
                    )
                )
            )),

            getAllGoods: rxMethod<void>(pipe(
                tap(() => patchState(store, <NonNullable<unknown>>{ ...setPropLoading('getAllGoods') })),
                switchMap(() =>
                    guardPermission(
                        PermissionNumber.ProdutoObterTodos,
                        'PRODUCT_LIST',
                        () => patchState(store, <NonNullable<unknown>>{ ...setPropError('getAllGoods', new Error('ALERT.ERROR_401')) }),
                        () => goodsService.getGoods({
                            $filter: 'IsActive eq true',
                            $orderby: 'Description',
                            $select: 'GoodId,Description,AmountUnitSale,BarCode,QuantityStock,IsActive',
                        })
                            .pipe(
                                tapResponse({
                                    next: ({ value }) => patchState(store, { allGoods: value }, setPropLoaded('getAllGoods')),
                                    error: (error: HttpErrorResponse) => {
                                        patchState( store, <NonNullable<unknown>>{ ...setPropError('getAllGoods', error) });
                                        errorStore.setHttpError(error)
                                    }
                                })
                            )
                    )
                )
            )),
        })
    }),
    withReqState(
      'getGood', 'createGood', 'updateGood', 'deleteGood',
      'getPurchase', 'createPurchase', 'updatePurchase', 'deletePurchase',
      'getSale', 'submitSale', 'deleteSale', 'getSaleConditions', 'getSaleSettings', 'updateSaleSettings',
      'getSupplier', 'updateSupplier', 'createSupplier',
      'getSaleCondition', 'createSaleCondition', 'updateSaleCondition', 'deleteSaleCondition',
      'getManualAdjustment', 'createManualAdjustment', 'updateManualAdjustment', 'deleteManualAdjustment', 'adjustStock',
      'submitInventoryBalance', 'getAllGoods'
    )
)
